/**
 * FileShareService - 文件分享（生成云端下载链接）
 *
 * 设计要点：
 * - 复用 apiClient：POST admin.wwls.net/api/local-file-share，自动带 Bearer token
 * - merchant_id 从 URL 参数取；seat 从 peerId 按最后一个 '-' 拆出前半段
 *   （peerId 格式: {seat}-{frp地址}，与 StatusBar 锁定/解锁逻辑一致）
 * - path 支持数组（多选），按 multipart 的 path[] 形式提交
 * - 接口响应 { code:0, data:{ token, url } }，url 形如 /api/local-file-share/{token}
 *   下载链接 = https://admin.wwls.net{url}/download[?password=xxx]
 */

import apiClient, { API_BASE } from './ApiClient';

// admin.wwls.net 站点根（API_BASE = https://admin.wwls.net/api）
const SITE_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

export interface ShareOptions {
  /** 文件提取密码，留空表示无密码 */
  password?: string;
  /** 最大下载次数，留空表示不限制 */
  maxDownloads?: number;
  /** 相对有效时长（秒），与 expiredAt 二选一 */
  expiresIn?: number;
  /** 绝对过期日期（YYYY-MM-DD），与 expiresIn 二选一 */
  expiredAt?: string;
}

export interface ShareResult {
  token: string;
  /** 接口返回的相对路径，如 /api/local-file-share/{token} */
  url: string;
  /** 不带密码的完整下载链接 */
  downloadUrl: string;
  /** 带密码的完整下载链接（仅当设置了密码时存在） */
  downloadUrlWithPassword?: string;
}

interface ShareApiResponse {
  code?: number;
  message?: string;
  data?: { token?: string; url?: string };
}

/** 从 URL 读取 merchant_id */
function getMerchantId(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('merchant_id');
  } catch {
    return null;
  }
}

/** 从 peerId 解析 seat（格式 {seat}-{frp地址}，取最后一个 '-' 前半段） */
function parseSeat(peerId: string | null | undefined): string | null {
  if (!peerId) return null;
  const idx = peerId.lastIndexOf('-');
  if (idx <= 0) return null;
  return peerId.substring(0, idx);
}

class FileShareService {
  /**
   * 创建文件分享，返回 token + 组装好的下载链接。
   * @param paths   选中的文件绝对路径（一个或多个）
   * @param peerId  当前会话 peerId（用于解析 seat）
   * @param opts    密码 / 最大下载次数 / 有效期
   */
  async createShare(paths: string[], peerId: string | null | undefined, opts: ShareOptions): Promise<ShareResult> {
    if (!paths || paths.length === 0) {
      throw new Error('未选择要分享的文件');
    }
    const merchantId = getMerchantId();
    if (!merchantId) {
      throw new Error('缺少 merchant_id 参数，无法分享');
    }
    const seat = parseSeat(peerId);
    if (!seat) {
      throw new Error('peer 信息缺失或格式异常，无法解析 seat');
    }

    const fd = new FormData();
    fd.append('merchant_id', merchantId);
    fd.append('seat', seat);
    // 多文件：path[] 数组形式提交
    for (const p of paths) {
      fd.append('path[]', p);
    }
    if (opts.password) fd.append('password', opts.password);
    if (opts.maxDownloads != null && opts.maxDownloads > 0) {
      fd.append('max_downloads', String(opts.maxDownloads));
    }
    // expires_in 与 expired_at 二选一（调用方保证只传一个）
    if (opts.expiresIn != null && opts.expiresIn > 0) {
      fd.append('expires_in', String(opts.expiresIn));
    } else if (opts.expiredAt) {
      fd.append('expired_at', opts.expiredAt);
    }

    const resp = await apiClient.request<ShareApiResponse>('/local-file-share', {
      method: 'POST',
      formData: fd,
    });

    const data = resp?.data;
    if (!data?.token || !data?.url) {
      throw new Error(resp?.message || '分享接口返回异常');
    }

    const base = `${SITE_ORIGIN}${data.url}/download`;
    const result: ShareResult = {
      token: data.token,
      url: data.url,
      // 设了密码时，不带密码链接也带空 password 参数（?password=）
      downloadUrl: opts.password ? `${base}?password=` : base,
    };
    if (opts.password) {
      result.downloadUrlWithPassword = `${base}?password=${encodeURIComponent(opts.password)}`;
    }
    return result;
  }
}

export const fileShareService = new FileShareService();
export default fileShareService;
